import React, { useMemo, useState } from 'react'
import logoImage from '../assets/pdf-generation/PB_Logo.png'
import signImage from '../assets/pdf-generation/PB_Sign.png'
import stampImage from '../assets/pdf-generation/PB_Stamp.png'
import { useTemplatePreviewSettings } from '../context/TemplatePreviewContext'
import './PdfGenerationStudio.css'

const templateOptions = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'purchaseOrder', label: 'Purchase Order' },
  { id: 'quotation', label: 'Quotation' }
]

const blankRowsByTemplate = {
  invoice: 8,
  purchaseOrder: 1,
  quotation: 8
}

const initialDraft = {
  template: 'invoice',
  companyName: 'PROBARA GIGIH TRADING (003614861-V)',
  companyAddress1: '(IMG2), JALAN SALAK TINGGI',
  companyAddress2: 'KAMPUNG JENDERAM HULU, 43800 DENGKIL, SELANGOR',
  companyAddress3: 'Email : probaragigih@gmail.com',
  clientName: 'Apex Retail Sdn. Bhd.',
  registrationNo: '202401234567',
  addressLine1: 'Level 18, Menara Apex',
  addressLine2: 'No. 8 Jalan Sultan Ismail',
  addressLine3: '50250 Kuala Lumpur',
  addressLine4: 'Malaysia',
  invoiceNo: 'INV-2026-001',
  invoiceDate: '2026-07-10',
  poNo: 'PO-4500-88',
  poIssuedDate: '2026-07-01',
  issuedDate: '2026-07-10',
  referenceNo: 'RFQ-7781',
  remarks: 'Phase 1',
  paymentTerm: '30 DAYS',
  projectNo: 'PGT-021',
  projectName: 'Retail Storefront Upgrade',
  recipientName: 'En. Mohamad Safwan',
  paymentMilestone: '50% upon design sign-off, 50% upon handover.',
  notes:
    'By proceeding with this payment, you agree to the terms and conditions outlined in this invoice.',
  quotationTerms:
    'By proceeding with this PO, you agree to the terms and conditions outlined in this PO',
  totalPoAmount: '25000',
  previousClaimAmount: '5000',
  creditNotePercent: '0',
  creditNoteAmount: '0',
  progressPercent: '35',
  items: [
    { description: 'Site survey and measurement', price: '1200', qty: '1' },
    { description: 'Material procurement and delivery', price: '4500', qty: '1' },
    { description: 'Installation and finishing works', price: '3000', qty: '1' }
  ]
}

function toNumber(value) {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

function formatCurrency(value) {
  return toNumber(value).toLocaleString('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function formatDate(value) {
  if (!value) return '-'
  return value
}

export function normalizeItems(items) {
  return items.map((item, index) => {
    const price = toNumber(item.price)
    const qty = toNumber(item.qty)
    const amount = price * qty

    return {
      id: index + 1,
      description: item.description,
      price,
      qty,
      amount
    }
  })
}

export function previewRows(items, template) {
  const totalRows = blankRowsByTemplate[template]
  const rows = [...items]

  while (rows.length < totalRows) {
    rows.push({
      id: `blank-${rows.length}`,
      description: '',
      price: '',
      qty: '',
      amount: ''
    })
  }

  return rows.slice(0, totalRows)
}

function PGLogo() {
  return <img src={logoImage} className="pg-logo" alt="" aria-hidden="true" />
}

function SignatureMark() {
  return <img src={signImage} className="signature-mark" alt="" aria-hidden="true" />
}

function StampMark() {
  return <img src={stampImage} className="stamp-mark" alt="" aria-hidden="true" />
}

function DocumentHeader({ draft, title }) {
  return (
    <div className="doc-header">
      <div>
        <h2 className="company-title">{draft.companyName}</h2>
        <p className="company-meta">{draft.companyAddress1}</p>
        <p className="company-meta">{draft.companyAddress2}</p>
        <p className="company-meta">{draft.companyAddress3}</p>
      </div>
      <div className="doc-header-logo">
        <PGLogo />
      </div>
      <div className="doc-divider" />
      <h1 className="doc-title">{title}</h1>
    </div>
  )
}

export function InvoicePreview({ draft, rows, totals }) {
  return (
    <div className="template-sheet">
      <DocumentHeader draft={draft} title="INVOICE" />

      <div className="invoice-top-grid">
        <div>
          <p className="stack-line strong">{draft.clientName}</p>
          <p className="stack-line">{draft.registrationNo}</p>
          <p className="stack-line">{draft.addressLine1}</p>
          <p className="stack-line">{draft.addressLine2}</p>
          <p className="stack-line">{draft.addressLine3}</p>
          <p className="stack-line">{draft.addressLine4}</p>
        </div>
        <dl className="info-list">
          <div><dt>INVOICE NO</dt><dd>{draft.invoiceNo}</dd></div>
          <div><dt>INVOICE NO DATE</dt><dd>{formatDate(draft.invoiceDate)}</dd></div>
          <div><dt>YOUR REFFERENCE NO.</dt><dd>{draft.poNo}</dd></div>
          <div><dt>PAYMENT TERM</dt><dd>{draft.paymentTerm}</dd></div>
        </dl>
      </div>

      <div className="project-row">
        <strong>Project Name:</strong>
        <span>{draft.projectName}</span>
      </div>

      <table className="doc-table compact invoice-table">
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '45%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="narrow">NO</th>
            <th>DESCRIPTION</th>
            <th className="number-col">PRICE</th>
            <th className="number-col">QTY</th>
            <th className="number-col">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, index) => (
            <tr key={item.id || index}>
              <td className="narrow">{item.description ? index + 1 : ''}</td>
              <td>{item.description}</td>
              <td className="number-col">{item.price === '' ? '' : formatCurrency(item.price)}</td>
              <td className="number-col">{item.qty}</td>
              <td className="number-col">{item.amount === '' ? '' : formatCurrency(item.amount)}</td>
            </tr>
          ))}
          <tr className="summary-row">
            <td colSpan="4">Total of PO Amount</td>
            <td className="number-col">{formatCurrency(draft.totalPoAmount)}</td>
          </tr>
          <tr className="summary-row">
            <td colSpan="4">Credit Note ({draft.creditNotePercent || 0}%)</td>
            <td className="number-col">{formatCurrency(draft.creditNoteAmount || 0)}</td>
          </tr>
          <tr className="summary-row">
            <td colSpan="4">Previous Claim Amount</td>
            <td className="number-col">{formatCurrency(draft.previousClaimAmount)}</td>
          </tr>
          <tr className="summary-row strong-row">
            <td colSpan="4">Total Claimable Amount ({draft.progressPercent}%)</td>
            <td className="number-col">{formatCurrency(totals.totalAmount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="terms-block">
        <strong>Term &amp; condition</strong>
        <p>{draft.notes}</p>
      </div>

      <div className="payment-block">
        <p>Please be informed that payment is to be made to :</p>
        <p className="strong">PROBARA GIGIH TRADING</p>
        <p className="strong">Acc No : 5623 7543 6845</p>
        <p className="strong">MALAYAN BANKING BERHAD (MAYBANK)</p>
      </div>

      <div className="signature-block">
        <div>
          <p>Yours faithfully,</p>
          <div className="signature-row">
            <SignatureMark />
            <StampMark />
          </div>
          <p className="strong signature-company">PROBARA GIGIH TRADING</p>
          <p>Phone No. : +60 19 909 0277</p>
        </div>
      </div>
    </div>
  )
}

export function PurchaseOrderPreview({ draft, items, totals }) {
  return (
    <div className="template-sheet">
      <DocumentHeader draft={draft} title="PURCHASE ORDER" />

      <div className="dual-box-grid">
        <div className="outlined-box">
          <p className="box-title">To :</p>
          <p className="stack-line strong">{draft.clientName}</p>
          <p className="stack-line">{draft.registrationNo}</p>
        </div>
        <div className="outlined-box">
          <p className="stack-line">Purchase Order : {draft.poNo}</p>
          <p className="stack-line">Purchase Order Date : {formatDate(draft.poIssuedDate)}</p>
          <p className="stack-line">Reference No : {draft.remarks}</p>
          <p className="stack-line">Payment Term : {draft.paymentTerm}</p>
        </div>
      </div>

      <table className="doc-table po-table">
        <colgroup>
          <col style={{ width: '6%' }} />
          <col style={{ width: '48%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="narrow">NO</th>
            <th>DESCRIPTION</th>
            <th className="number-col">QUANTITY</th>
            <th className="number-col">UNIT</th>
            <th className="number-col">UNIT PRICE (RM)</th>
            <th className="number-col">TOTAL (RM)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="po-main-row">
            <td className="narrow">1</td>
            <td>
              <p className="strong">Project Title :</p>
              <p>{draft.projectName}</p>
              <p className="strong section-spacer">Item Description :</p>
              {items.map((item, index) => (
                <p key={item.id}>{index + 1}) {item.description}</p>
              ))}
              <p className="strong section-spacer">Payment Milestone &amp; Term :</p>
              <p>{draft.paymentMilestone}</p>
            </td>
            <td className="number-col">{totals.totalQty}</td>
            <td className="number-col">NA</td>
            <td className="number-col">{formatCurrency(totals.unitPriceAverage)}</td>
            <td className="number-col">{formatCurrency(totals.totalAmount)}</td>
          </tr>
          <tr className="summary-row strong-row">
            <td colSpan="5" className="centered">TOTAL ORDER VALUE</td>
            <td className="number-col">{formatCurrency(totals.totalAmount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="po-signatures">
        <div>
          <p>Authorized Signature</p>
          <p className="strong">For PROBARA GIGIH TRADING</p>
          <SignatureMark />
        </div>
        <div>
          <p>Receive By :</p>
          <p>Customer Signature &amp; Company Stamp</p>
          <p className="date-line">Date : ________________</p>
        </div>
      </div>
    </div>
  )
}

export function QuotationPreview({ draft, rows, totals }) {
  return (
    <div className="template-sheet">
      <DocumentHeader draft={draft} title="QUOTATION" />

      <div className="invoice-top-grid quotation-top-grid">
        <div>
          <p className="stack-line"><strong>To :</strong> {draft.recipientName}</p>
        </div>
        <dl className="info-list">
          <div><dt>Purchase Order No.</dt><dd>{draft.poNo}</dd></div>
          <div><dt>Purchase Order Date</dt><dd>{formatDate(draft.issuedDate)}</dd></div>
          <div><dt>Reference No</dt><dd>{draft.referenceNo}</dd></div>
          <div><dt>Payment term</dt><dd>{draft.paymentTerm}</dd></div>
        </dl>
      </div>

      <div className="project-row">
        <strong>Project Name :</strong>
        <span>{draft.projectName}</span>
      </div>

      <table className="doc-table compact quotation-table">
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '45%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="narrow">NO</th>
            <th>DESCRIPTION</th>
            <th className="number-col">PRICE</th>
            <th className="number-col">QTY</th>
            <th className="number-col">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, index) => (
            <tr key={item.id || index}>
              <td className="narrow">{item.description ? index + 1 : ''}</td>
              <td>{item.description}</td>
              <td className="number-col">{item.price === '' ? '' : formatCurrency(item.price)}</td>
              <td className="number-col">{item.qty}</td>
              <td className="number-col">{item.amount === '' ? '' : formatCurrency(item.amount)}</td>
            </tr>
          ))}
          <tr className="summary-row strong-row">
            <td colSpan="4" className="centered">Total Amount</td>
            <td className="number-col">{formatCurrency(totals.totalAmount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="terms-block quotation-terms">
        <strong>Term &amp; condition</strong>
        <p>{draft.quotationTerms}</p>
      </div>

      <div className="signature-block quotation-signature">
        <p className="strong">Authorised Signature</p>
        <p className="strong">PROBARA GIGIH TRADING</p>
        <div className="signature-row">
          <SignatureMark />
          <StampMark />
        </div>
      </div>
    </div>
  )
}

function LivePreview({ draft, items, totals }) {
  const rows = previewRows(items, draft.template)

  if (draft.template === 'purchaseOrder') {
    return <PurchaseOrderPreview draft={draft} items={items} totals={totals} />
  }

  if (draft.template === 'quotation') {
    return <QuotationPreview draft={draft} rows={rows} totals={totals} />
  }

  return <InvoicePreview draft={draft} rows={rows} totals={totals} />
}

export default function PdfGenerationStudio() {
  const [template, setTemplate] = useState(initialDraft.template)
  const { previewZoom, tableDensity, fontScale } = useTemplatePreviewSettings()

  const draft = useMemo(() => ({ ...initialDraft, template }), [template])
  const items = useMemo(() => normalizeItems(draft.items), [draft.items])
  const totals = useMemo(() => {
    const totalAmount = items.reduce((sum, item) => sum + item.amount, 0)
    const totalQty = items.reduce((sum, item) => sum + item.qty, 0)
    const unitPriceAverage = totalQty > 0 ? totalAmount / totalQty : 0

    return {
      totalAmount,
      totalQty,
      unitPriceAverage
    }
  }, [items])

  return (
    <div className="pdf-studio-root space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Template selector</p>
        <div className="template-selector mt-3" role="tablist" aria-label="Document templates">
          {templateOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              className={option.id === template ? 'template-pill active' : 'template-pill'}
              onClick={() => setTemplate(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <section className="preview-panel">
        <div className={`template-preview-theme template-density-${tableDensity} preview-stack`} style={{ '--template-font-scale': fontScale }}>
          <div className="preview-card preview-card-document" style={{ zoom: previewZoom }}>
            <LivePreview draft={draft} items={items} totals={totals} />
          </div>
        </div>
      </section>
    </div>
  )
}
